import { useParams } from 'react-router';
import jwtDecode from 'jwt-decode';
import { JwtClaims } from 'oidc-client-ts';
import { useDispatch } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@pmi/dsm-react';
import BlockIcon from '@pmi/dsm-react/dist/components/icons/16/BlockIcon';
import useUrlSearchParams from '../../hooks/useUrlSearchParams';
import { setCurrentAccount } from '../../store/shared/accountSlice';
import { useGetAccountListQuery } from '../../../Admin/AccountSelect/apiSlice';
import {
  getOidcAuthority,
  getOidcClientId,
  getOidcToken,
} from '../../env-vars';
import { AccountSelectDto } from '../../../Admin/AccountSelect/models';
import {
  useResolveClientTokenMutation,
  useValidateTokenMutation,
} from './apiSlice';
import { showSidebar } from '../../../Admin/adminLayoutSlice';
import AuthService from '../../services/AuthService';

const EMPTY_ACCOUNT_LIST: AccountSelectDto[] = [];

export default function TwoFactorAuthCallback() {
  const params = useParams();
  const urlSearchParams = useUrlSearchParams();
  const tokenFromQuery = urlSearchParams.get('token')?.toString();
  const clientTokenId = urlSearchParams.get('code')?.toString();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authService = new AuthService();

  const { powerUserRoleAssignmentId, accountList, loading, error, success } =
    useGetAccountListQuery(
      {
        accountId: params.accountId,
      },
      {
        selectFromResult: ({
          data: dataResponse,
          isLoading,
          isError,
          isSuccess,
        }) => {
          const filteredData: AccountSelectDto[] =
            dataResponse?.accounts.items ?? EMPTY_ACCOUNT_LIST;

          return {
            powerUserRoleAssignmentId: dataResponse?.powerUserRoleAssignmentId,
            accountList: filteredData,
            loading: isLoading,
            error: isError,
            success: isSuccess,
          };
        },
      },
    );
  const [errorMessage, setErrorMessage] = useState(null);
  const [resolveClientToken] = useResolveClientTokenMutation();
  const [validateToken] = useValidateTokenMutation();
  const accountListKey = accountList
    .map(
      ({ accountId, roleAssignmentId }) => `${accountId}:${roleAssignmentId}`,
    )
    .join('|');

  const removeCodeParameterFromUrl = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('code');
    window.history.replaceState({}, '', currentUrl.toString());
  };

  const setBackNavigationToSelectAccount = () => {
    window.history.replaceState({}, '', '/admin/select-account');
  };

  useEffect(() => {
    dispatch(showSidebar(false));
  }, []);

  useEffect(() => {
    if (!success) return;

    const processToken = async () => {
      let token = tokenFromQuery;

      if (clientTokenId) {
        try {
          const tokenResponse = await resolveClientToken({
            clientTokenId,
            routeType: 'rbac',
          }).unwrap();
          token = tokenResponse?.token;
        } catch {
          setErrorMessage('The token is invalid!');
          return;
        } finally {
          removeCodeParameterFromUrl();
        }
      }

      if (!token) {
        setErrorMessage('The token is invalid!');
        return;
      }

      try {
        const jwt = jwtDecode<JwtClaims>(token, { header: false });
        const rbac2fa = jwt.rbac_2fa_raids;
        if (
          !(
            accountList.some(
              (x) =>
                x.roleAssignmentId === rbac2fa &&
                x.accountId.toLowerCase() === params.accountId.toLowerCase(),
            ) || powerUserRoleAssignmentId === rbac2fa
          )
        ) {
          setErrorMessage(
            'Account & Role Assignment mismatch. Please make sure you are authorized to access this account.',
          );
          return;
        }
        const storedToken = JSON.parse(localStorage.getItem(getOidcToken()));
        if (jwt.iss !== getOidcAuthority() && jwt.aud !== getOidcClientId()) {
          setErrorMessage('The token is invalid!');
        } else {
          validateToken(token)
            .unwrap()
            .then(async () => {
              const newToken = {
                ...storedToken,
                access_token: token,
                expires_at: jwt.exp,
              };
              localStorage.setItem(getOidcToken(), JSON.stringify(newToken));
              if (
                window.opener &&
                window.opener !== window &&
                !window.menubar.visible
              ) {
                await authService.loginPopupCallback();
                return;
              }

              setBackNavigationToSelectAccount();
              dispatch(setCurrentAccount(params.accountId));

              if (params.dealId) {
                navigate(`/admin/${params.accountId}/${params.dealId}`);
              } else {
                navigate(`/admin/${params.accountId}`);
              }
            })
            .catch(() => {
              setErrorMessage(
                'Unauthorized! Please make sure to verify your Account info or select a different Account',
              );
            });
        }
      } catch {
        setErrorMessage('The token is invalid!');
      }
    };

    processToken();
  }, [
    success,
    tokenFromQuery,
    clientTokenId,
    powerUserRoleAssignmentId,
    accountListKey,
  ]);

  if (loading) {
    return (
      <div className="p-5">
        <Spinner className="d-block m-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="box">
        <h3 className="text-center">
          There was an error validating your request. Make sure you are logged
          in to view this area.
        </h3>
      </div>
    );
  }

  return (
    <div className="box">
      {errorMessage ? (
        <Alert
          variant="danger"
          onClose={() => navigate('/')}
          show
          alertText={errorMessage}
          onActionableLinkClick={() => navigate('/admin/select-account')}
          actionableLinkText="Select A Different Account"
          dismissButtonLabel="Close"
          onDismissButtonClick={() => navigate('/')}
          icon={BlockIcon}
        />
      ) : (
        <div className="box">
          <h3 className="text-center" data-testid="redirect-text">
            Redirecting to your account...
          </h3>
        </div>
      )}
    </div>
  );
}
